import { StyleSheet } from '@react-pdf/renderer';
import { SQUAD_COLORS } from '../templateConstants';
import {
  SQUAD_PAGE_HEADER,
  SQUAD_BODY_LAYOUT,
  SQUAD_TYPOGRAPHY_PDF,
  SQUAD_SECTION_GEOMETRY_PDF,
} from '../pageHeaderTokens';
import { SQUAD_ADVISOR_PHOTO } from '../squadAdvisors';
import { SQUAD_IMAGE_FORMATS } from '../squadImageFormats';
import { SQUAD_VECTOR_PIE } from '../squadVectorDonut';
import {
  SQUAD_BAR_HEIGHT_PDF_PT,
  SQUAD_BAR_LABEL_BAR_GAP_PDF_PT,
  SQUAD_BAR_LABEL_COLUMN_PDF_PT,
  SQUAD_BAR_ROW_MARGIN_PDF_PT,
  SQUAD_BAR_ROW_HEIGHT_PDF_PT,
} from '../squadPortfolioChartUtils';

const { sidebarMaxWidth, columnGap } = SQUAD_BODY_LAYOUT;

/** A4 width (pt) — used so header can bleed into the sidebar column. */
const PDF_A4_WIDTH_PT = 595.28;
const PDF_PAGE_PAD_H = SQUAD_PAGE_HEADER.pdfPagePaddingHorizontal;
/** Sidebar footnote leading and padding — shared so the logo above can keep clear. */
export const SQUAD_FOOTNOTE_LINE_HEIGHT = 1.3;
export const SQUAD_FOOTNOTE_PADDING_V_PT = 3;
/** Drawable width inside the tan rail. */
const SIDEBAR_INNER_WIDTH_PT =
  SQUAD_BODY_LAYOUT.sidebarMaxWidth - SQUAD_PAGE_HEADER.pdfSidebarPaddingHorizontal * 2;
const PDF_SIDEBAR_INSET = sidebarMaxWidth + columnGap;
/** Page paddingLeft so wrapped main content never draws under the sidebar rail. */
const PDF_PAGE_PAD_LEFT_WITH_SIDEBAR = PDF_PAGE_PAD_H + PDF_SIDEBAR_INSET;
/** Header spans logo (sidebar) + title (main). */
const PDF_HEADER_FULL_INNER_WIDTH = PDF_A4_WIDTH_PT - PDF_PAGE_PAD_H * 2;
/** Logo column height (SQUAD FONDS mark). */
const PDF_LOGO_BLOCK_HEIGHT = SQUAD_PAGE_HEADER.logoImageHeightPdf;
/**
 * Title + meta row height (meta lineHeight buffer so descenders are not clipped).
 * Depends on the title size actually used — long fund names shrink the title
 * (see squadTitleFit), otherwise the first section bar covers the title line.
 */
const pdfTitleBlockHeight = (titleFontSize: number) =>
  Math.ceil(titleFontSize * SQUAD_PAGE_HEADER.titleLineHeight) +
  SQUAD_PAGE_HEADER.titleRowMetaGap +
  Math.ceil(SQUAD_PAGE_HEADER.metaFontSizePdf * 1.2);

export const getPdfStyles = (
  primary: string = SQUAD_COLORS.navy,
  secondary: string = SQUAD_COLORS.gold,
  titleFontSize: number = SQUAD_PAGE_HEADER.titleFontSize,
) => {
  /**
   * Shared header row height so mainCol (first navy bar) and tan sidebar
   * start on the same horizontal line under the logo/title.
   */
  const PDF_HEADER_ROW_HEIGHT = Math.max(PDF_LOGO_BLOCK_HEIGHT, pdfTitleBlockHeight(titleFontSize));
  /** Top of the main column's content box, below the fixed logo/title header. */
  const PDF_SIDEBAR_TOP =
    SQUAD_PAGE_HEADER.pdfPagePaddingTop +
    PDF_HEADER_ROW_HEIGHT +
    SQUAD_PAGE_HEADER.pdfHeaderMarginBottom;
  /**
   * Top of the tan rail and of the sidebar riding in it. The first main-column bar
   * starts navyBarMarginTop below the content box, so the rail has to start there
   * too — otherwise a sliver of tan shows above that bar.
   */
  const PDF_RAIL_TOP = PDF_SIDEBAR_TOP + SQUAD_SECTION_GEOMETRY_PDF.navyBarMarginTop;

  /** Sidebar body copy is always brand navy — not fund FS-Header primary (e.g. Praemium #1F4B8D). */
  const sidebarText = SQUAD_COLORS.navy;

  return StyleSheet.create({
    /**
     * Disclaimer page: no sidebar rail, but a fixed header like the other pages.
     * The header must be `fixed` for the dynamic meta labels ("Werbemitteilung" /
     * "Seite X von Y") to render at all — in normal flow react-pdf drops `render`
     * texts. paddingTop reserves the space the header no longer occupies in flow.
     */
    pageFullWithFixedHeader: {
      paddingTop: PDF_SIDEBAR_TOP,
      paddingBottom: SQUAD_PAGE_HEADER.pdfPagePaddingBottom,
      paddingHorizontal: PDF_PAGE_PAD_H,
      fontFamily: 'SegoeUI',
      fontSize: SQUAD_TYPOGRAPHY_PDF.base,
      color: SQUAD_COLORS.text,
      backgroundColor: SQUAD_COLORS.white,
      flexDirection: 'column',
    },
    /** Full-bleed page (e.g. page 4 disclaimer — no sidebar). */
    page: {
      paddingTop: SQUAD_PAGE_HEADER.pdfPagePaddingTop,
      paddingBottom: SQUAD_PAGE_HEADER.pdfPagePaddingBottom,
      paddingHorizontal: PDF_PAGE_PAD_H,
      fontFamily: 'SegoeUI',
      fontSize: SQUAD_TYPOGRAPHY_PDF.base,
      color: SQUAD_COLORS.text,
      backgroundColor: SQUAD_COLORS.white,
      flexDirection: 'column',
    },
    /**
     * Pages 1–3: main column is permanently inset.
     * paddingTop reserves space for the fixed logo/title header on every wrapped page.
     */
    pageWithSidebar: {
      paddingTop: PDF_SIDEBAR_TOP,
      paddingBottom: SQUAD_PAGE_HEADER.pdfPagePaddingBottom,
      paddingRight: PDF_PAGE_PAD_H,
      paddingLeft: PDF_PAGE_PAD_LEFT_WITH_SIDEBAR,
      fontFamily: 'SegoeUI',
      fontSize: SQUAD_TYPOGRAPHY_PDF.base,
      color: SQUAD_COLORS.text,
      backgroundColor: SQUAD_COLORS.white,
      flexDirection: 'column',
    },
    pageHeader: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: columnGap,
      minHeight: PDF_HEADER_ROW_HEIGHT,
      marginBottom: SQUAD_PAGE_HEADER.pdfHeaderMarginBottom,
      paddingBottom: SQUAD_PAGE_HEADER.headerPaddingBottom,
    },
    /** Fixed wrapper — logo + title repeat on overflow pages. */
    pageHeaderFixed: {
      position: 'absolute',
      left: PDF_PAGE_PAD_H,
      top: SQUAD_PAGE_HEADER.pdfPagePaddingTop,
      width: PDF_HEADER_FULL_INNER_WIDTH,
    },
    /** Pages 1–3: logo in left column, title in main (used inside pageHeaderFixed). */
    pageHeaderWithSidebar: {
      width: PDF_HEADER_FULL_INNER_WIDTH,
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: columnGap,
      minHeight: PDF_HEADER_ROW_HEIGHT,
      marginBottom: 0,
      paddingBottom: SQUAD_PAGE_HEADER.headerPaddingBottom,
    },
    headerSidebarCell: {
      width: sidebarMaxWidth,
      flexShrink: 0,
      alignSelf: 'flex-start',
      alignItems: 'flex-start',
      minHeight: PDF_HEADER_ROW_HEIGHT,
    },
    headerMainCell: {
      flex: 1,
      flexDirection: 'column',
      gap: SQUAD_PAGE_HEADER.titleRowMetaGap,
      alignSelf: 'flex-start',
      minHeight: PDF_HEADER_ROW_HEIGHT,
      justifyContent: 'flex-start',
    },
    headerMetaRow: {
      width: '100%',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
    },
    headerMetaText: {
      fontSize: SQUAD_PAGE_HEADER.metaFontSizePdf,
      color: SQUAD_PAGE_HEADER.metaColor,
      fontFamily: 'SegoeUI',
      lineHeight: 1,
    },
    logoBlock: {
      width: SQUAD_PAGE_HEADER.logoBlockWidthPdf,
      alignSelf: 'flex-start',
      flexDirection: 'column',
      alignItems: 'stretch',
      marginBottom: SQUAD_PAGE_HEADER.pdfLogoMarginBottom,
      height: PDF_LOGO_BLOCK_HEIGHT,
    },
    mainTitle: {
      fontSize: titleFontSize,
      textAlign: 'left',
      lineHeight: SQUAD_PAGE_HEADER.titleLineHeight,
      letterSpacing: SQUAD_PAGE_HEADER.titleLetterSpacing,
    },
    /**
     * Both title parts share one weight — the original factsheet does too.
     * Semibold rather than bold: Segoe UI Bold runs heavier than the Corbel Bold
     * the original uses for its title.
     */
    titleSquad: { color: secondary, fontFamily: 'SegoeUI', fontWeight: 600 },
    titleRest: { color: primary, fontFamily: 'SegoeUI', fontWeight: 600 },
    /** Tan rail — top aligns with first main section navy bar. */
    pageSidebarRail: {
      position: 'absolute',
      left: PDF_PAGE_PAD_H,
      top: PDF_RAIL_TOP,
      bottom: SQUAD_PAGE_HEADER.pdfPagePaddingBottom,
      width: sidebarMaxWidth,
      backgroundColor: SQUAD_COLORS.sidebarBg,
    },
    /**
     * Sidebar widgets — absolute in the rail (first page only).
     * Top matches rail / first navy bar; no extra paddingTop so titles align.
     */
    sidebar: {
      position: 'absolute',
      left: PDF_PAGE_PAD_H,
      top: PDF_RAIL_TOP,
      width: sidebarMaxWidth,
      backgroundColor: 'transparent',
      paddingHorizontal: SQUAD_PAGE_HEADER.pdfSidebarPaddingHorizontal,
      paddingTop: SQUAD_PAGE_HEADER.pdfSidebarPaddingTop,
      paddingBottom: SQUAD_PAGE_HEADER.pdfSidebarPaddingBottom,
      flexDirection: 'column',
    },
    /**
     * Foot of the tan sidebar (also on overflow pages via `fixed`): the optional page-2 logo
     * and the page's footnotes, stacked. Both sit in one absolute block, so the logo always
     * clears the notes, however many lines they wrap to.
     */
    sidebarBottomStack: {
      position: 'absolute',
      left: PDF_PAGE_PAD_H,
      bottom: SQUAD_PAGE_HEADER.pdfPagePaddingBottom,
      width: sidebarMaxWidth,
    },
    sidebarFootnote: {
      paddingHorizontal: SQUAD_PAGE_HEADER.pdfSidebarPaddingHorizontal,
      paddingTop: SQUAD_FOOTNOTE_PADDING_V_PT,
      paddingBottom: SQUAD_PAGE_HEADER.pdfSidebarPaddingBottom,
    },
    /** Main column — grow to fill page so footer (ratings) can sit at bottom. */
    mainCol: {
      gap: 5,
      width: '100%',
      flexGrow: 1,
      flexDirection: 'column',
    },
    /** Monatskommentar expands so awards/disclaimer stay at page bottom. */
    mainSectionGrow: {
      marginBottom: 0,
      flexGrow: 1,
      flexShrink: 1,
    },
    mainSectionFooter: {
      marginBottom: 0,
      marginTop: 'auto',
      flexShrink: 0,
    },
    section: { marginBottom: 6, flexShrink: 0 },
    mainSection: { marginBottom: 0, flexShrink: 0 },
    /** Two pie widgets side-by-side in main column. */
    pieChartsRow: {
      flexDirection: 'row',
      gap: 8,
      alignItems: 'flex-start',
      width: '100%',
    },
    pieChartSlot: {
      width: '50%',
      maxWidth: '50%',
      minWidth: 0,
      flexGrow: 0,
      flexShrink: 0,
    },
    /**
     * Chart absolutely on the left; legend always inset by chart width.
     * Overflow pages keep that inset so legends stay right-aligned.
     */
    pieBlockRow: {
      width: '100%',
      position: 'relative',
      minHeight: 121,
    },
    pieBlockChartCol: {
      position: 'absolute',
      left: 0,
      top: 0,
      width: 121,
    },
    pieBlockChartImage: {
      width: 121,
      height: 121,
      objectFit: 'contain',
    },
    pieBlockLegendCol: {
      marginLeft: 126,
      minWidth: 0,
    },
    pieLegendRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 2,
      gap: 3,
    },
    pieLegendMark: {
      width: 6,
      height: 6,
      marginTop: 1.5,
      flexShrink: 0,
    },
    pieLegendLabel: {
      flex: 1,
      minWidth: 0,
      fontSize: SQUAD_TYPOGRAPHY_PDF.bar,
      color: 'rgb(12, 28, 61)',
      lineHeight: 1.15,
    },
    /** Vector donut (VectorPieChartBlock): ring left, legend centred beside it. */
    vectorPieRow: {
      flexDirection: 'row',
      alignItems: 'center',
      width: '100%',
    },
    vectorPieLegend: {
      flex: 1,
      minWidth: 0,
      marginLeft: SQUAD_VECTOR_PIE.gapPt,
    },
    /** Value column of the vector-donut legend, in front of the label — right-aligned so the figures line up. */
    pieLegendValue: {
      width: SQUAD_VECTOR_PIE.valueColumnPt,
      flexShrink: 0,
      textAlign: 'right',
      fontFamily: 'SegoeUI', fontWeight: 700,
      fontSize: SQUAD_TYPOGRAPHY_PDF.bar,
      color: 'rgb(12, 28, 61)',
      lineHeight: 1.15,
    },
    goldBar: {
      backgroundColor: 'transparent',
      color: sidebarText,
      fontSize: SQUAD_TYPOGRAPHY_PDF.goldBar,
      fontFamily: 'SegoeUI', fontWeight: 700,
      textAlign: 'center',
      paddingTop: SQUAD_SECTION_GEOMETRY_PDF.goldBarPaddingTop,
      paddingBottom: SQUAD_SECTION_GEOMETRY_PDF.goldBarPaddingBottom,
      marginTop: SQUAD_SECTION_GEOMETRY_PDF.goldBarMarginTop,
      marginBottom: SQUAD_SECTION_GEOMETRY_PDF.goldBarMarginBottom,
      borderTopWidth: 1.5,
      borderBottomWidth: 1.5,
      borderTopColor: sidebarText,
      borderBottomColor: sidebarText,
    },
    navyBar: {
      backgroundColor: primary,
      color: SQUAD_COLORS.white,
      fontSize: SQUAD_TYPOGRAPHY_PDF.navyBar,
      fontFamily: 'SegoeUI', fontWeight: 700,
      textAlign: 'center',
      paddingTop: SQUAD_SECTION_GEOMETRY_PDF.navyBarPaddingTop,
      paddingBottom: SQUAD_SECTION_GEOMETRY_PDF.navyBarPaddingBottom,
      paddingHorizontal: 8,
      marginTop: SQUAD_SECTION_GEOMETRY_PDF.navyBarMarginTop,
      marginBottom: 5,
      borderRadius: 3,
    },
    kvRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingLeft: 0,
      paddingRight: 4,
      paddingVertical: SQUAD_SECTION_GEOMETRY_PDF.kvRowPaddingVertical,
      fontSize: SQUAD_TYPOGRAPHY_PDF.kv,
      borderBottomWidth: 0.5,
      borderBottomColor: sidebarText,
    },
    // Width lives on the cell View (not Text) so long labels wrap inside the column
    kvLabelCell: {
      /** 60/40 as in the original factsheet (measured 99 pt / 63 pt) — avoids wrapping
       *  of "Anteilsklassenvolumen" / "Mindestkapitalbeteiligungsquote". */
      width: '60%',
      paddingLeft: 0,
      paddingRight: 4,
      alignItems: 'flex-start',
    },
    kvValueCell: {
      width: '40%',
      alignItems: 'flex-start',
    },
    kvLabel: { color: sidebarText, textAlign: 'left', width: '100%' },
    /** Fund partner blocks — role, company, address, all on the content base. */
    partnerLine: {
      fontSize: SQUAD_TYPOGRAPHY_PDF.kontakt,
      color: sidebarText,
      lineHeight: 1.4,
    },
    kvValue: { color: sidebarText, textAlign: 'left', width: '100%' },
    bodyText: { fontSize: SQUAD_TYPOGRAPHY_PDF.body, lineHeight: 1.31, textAlign: 'justify', marginBottom: 0 },
    /** Anlagestrategie / Monatskommentar — tighter leading than general body. */
    proseText: {
      fontSize: SQUAD_TYPOGRAPHY_PDF.body,
      lineHeight: SQUAD_SECTION_GEOMETRY_PDF.proseLineHeight,
      textAlign: 'justify',
      marginBottom: 0,
    },
    /** Sidebar footnotes. The yellow ground was a note-to-self that the wording was
     *  still missing; it is not part of the design. */
    footnote: {
      fontSize: SQUAD_TYPOGRAPHY_PDF.footnote,
      lineHeight: SQUAD_FOOTNOTE_LINE_HEIGHT,
      color: sidebarText,
      paddingHorizontal: 0,
      // Padding belongs to the block, not to each line — on the line it would open a
      // gap between the notes that the height calculation does not know about.
      paddingVertical: 0,
    },
    placeholder: {
      borderWidth: 1,
      borderColor: secondary,
      borderStyle: 'dashed',
      backgroundColor: '#FAF6EE',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 10,
      marginVertical: 4,
    },
    placeholderText: { fontSize: SQUAD_TYPOGRAPHY_PDF.placeholder, color: SQUAD_COLORS.goldDark, textAlign: 'center' },
    barRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 3, fontSize: SQUAD_TYPOGRAPHY_PDF.bar },
    barLabel: { width: '40%' },
    barTrack: { flex: 1, height: 12, backgroundColor: '#EEE' },
    barFill: { height: 12, backgroundColor: secondary },
    barPct: { width: 36, textAlign: 'right', fontFamily: 'SegoeUI', fontWeight: 700 },
    portfolioBarRow: {
      flexDirection: 'row',
      alignItems: 'center',
      minHeight: SQUAD_BAR_ROW_HEIGHT_PDF_PT,
      marginBottom: SQUAD_BAR_ROW_MARGIN_PDF_PT,
      fontSize: SQUAD_TYPOGRAPHY_PDF.portfolioBar,
      color: 'rgb(12, 28, 61)',
    },
    portfolioBarLabelCell: {
      width: SQUAD_BAR_LABEL_COLUMN_PDF_PT,
      paddingRight: SQUAD_BAR_LABEL_BAR_GAP_PDF_PT,
      flexShrink: 0,
      justifyContent: 'center',
      minHeight: SQUAD_BAR_ROW_HEIGHT_PDF_PT,
    },
    portfolioBarLabel: {
      width: SQUAD_BAR_LABEL_COLUMN_PDF_PT - SQUAD_BAR_LABEL_BAR_GAP_PDF_PT,
      textAlign: 'right',
      fontSize: SQUAD_TYPOGRAPHY_PDF.portfolioBar,
      lineHeight: SQUAD_BAR_HEIGHT_PDF_PT / SQUAD_TYPOGRAPHY_PDF.portfolioBar,
    },
    portfolioBarTrack: {
      flex: 1,
      height: SQUAD_BAR_HEIGHT_PDF_PT,
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'center',
      minWidth: 0,
    },
    portfolioBarFill: {
      height: SQUAD_BAR_HEIGHT_PDF_PT,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingRight: 2,
    },
    /**
     * lineHeight must be tied to the bar height like the label's is: bar and track are
     * a fixed SQUAD_BAR_HEIGHT_PDF_PT tall, and Segoe UI's default line height exceeds
     * that at 8.64 pt — react-pdf then drops the text entirely (the percentages went
     * missing while the bars themselves were still drawn).
     */
    portfolioBarValueInside: {
      fontFamily: 'SegoeUI', fontWeight: 700,
      fontSize: SQUAD_TYPOGRAPHY_PDF.portfolioBar,
      lineHeight: SQUAD_BAR_HEIGHT_PDF_PT / SQUAD_TYPOGRAPHY_PDF.portfolioBar,
      color: 'rgb(12, 28, 61)',
    },
    portfolioBarValueOutside: {
      marginLeft: 4,
      fontFamily: 'SegoeUI', fontWeight: 700,
      fontSize: SQUAD_TYPOGRAPHY_PDF.portfolioBar,
      lineHeight: SQUAD_BAR_HEIGHT_PDF_PT / SQUAD_TYPOGRAPHY_PDF.portfolioBar,
      color: 'rgb(12, 28, 61)',
    },
    riskBox: {
      flex: 1,
      height: 18,
      borderWidth: 1,
      // Frame stays navy like the rest of the sidebar; only the active box takes the fund colour
      borderColor: sidebarText,
      alignItems: 'center',
      justifyContent: 'center',
      marginHorizontal: 0,
      fontSize: SQUAD_TYPOGRAPHY_PDF.riskBox,
      fontFamily: 'SegoeUI', fontWeight: 700,
    },
    riskBoxLinked: {
      marginLeft: -1,
    },
    riskScaleLabels: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 2,
    },
    riskScaleBoxes: {
      flexDirection: 'row',
      width: '100%',
    },
    riskArrowBar: {
      flexDirection: 'row',
      alignItems: 'center',
      width: '100%',
      marginVertical: 3,
    },
    riskArrowHead: {
      fontSize: 8,
      lineHeight: 1,
    },
    riskArrowLine: {
      flex: 1,
      height: 1.5,
      marginHorizontal: 1,
    },
    tableCell: { fontSize: SQUAD_TYPOGRAPHY_PDF.tableCell, padding: 3 },
    page4: { paddingTop: 4 },
    /**
     * Disclaimer page: same size as everywhere else (8.64 pt) but tighter leading.
     * On the base of 8 the text fit one page; at the original size it otherwise
     * spilled two orphan lines onto a following page.
     */
    disclaimerText: {
      fontSize: SQUAD_TYPOGRAPHY_PDF.footnote,
      lineHeight: 1.2,
      textAlign: 'justify',
      marginBottom: 0,
    },
    sidebarSectionTitle: {
      fontSize: SQUAD_TYPOGRAPHY_PDF.sidebarSection,
      fontFamily: 'SegoeUI', fontWeight: 700,
      textDecoration: 'underline',
      marginBottom: 6,
      color: sidebarText,
    },
    advisorBlock: { marginBottom: 4 },
    advisorBlockFeatured: { marginBottom: 5 },
    advisorBlockDuo: { marginTop: 2 },
    advisorFeaturedRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 2, gap: 6 },
    advisorFeaturedPhotoCol: { width: '46%' },
    advisorFeaturedLogoCol: {
      flex: 1,
      alignItems: 'flex-start',
      justifyContent: 'center',
      minHeight: Math.round(80 * (SQUAD_ADVISOR_PHOTO.heightPx / SQUAD_ADVISOR_PHOTO.widthPx)),
      paddingLeft: 3,
    },
    /**
     * 16:9 frame across the full sidebar width — same width as advisorFooterLogo and
     * the body text, so photo, logo and prose share one left and right edge.
     */
    advisorFeaturedPhoto: {
      width: SIDEBAR_INNER_WIDTH_PT,
      height: Math.round(SIDEBAR_INNER_WIDTH_PT / SQUAD_IMAGE_FORMATS.advisorPhoto.aspect),
      objectFit: 'contain',
      marginBottom: 4,
    },
    advisorFeaturedLogoLine1: { fontFamily: 'SegoeUI', fontSize: SQUAD_TYPOGRAPHY_PDF.advisorLogoLine1, color: sidebarText, textAlign: 'left' },
    advisorFeaturedLogoLine2: { fontFamily: 'SegoeUI', fontSize: SQUAD_TYPOGRAPHY_PDF.advisorLogoLine2, color: secondary, textAlign: 'left' },
    advisorDuoRow: { flexDirection: 'row', gap: 4, marginBottom: 2 },
    advisorDuoCol: { flex: 1 },
    advisorDuoPhoto: {
      width: '100%',
      height: Math.round(80 * (SQUAD_ADVISOR_PHOTO.heightPx / SQUAD_ADVISOR_PHOTO.widthPx)),
      objectFit: 'contain',
      marginBottom: 1,
    },
    /** 1280 × 540 frame (ca. 21:9) drawn edge to edge — the file carries its own margins. */
    advisorFooterLogo: {
      width: SIDEBAR_INNER_WIDTH_PT,
      height: Math.round(SIDEBAR_INNER_WIDTH_PT / SQUAD_IMAGE_FORMATS.partnerLogo.aspect),
      objectFit: 'contain',
      marginTop: 4,
      alignSelf: 'center',
    },
    /**
     * Optional logo just above the page-2 footnotes, inside `sidebarBottomStack` — pinned
     * to the page foot, so a longer contact block cannot push it off the page (SQUAD:
     * "dass das nicht auf die nächste Seite rutscht"). 6 pt gap to the notes, as before.
     */
    sidebarBottomLogo: {
      marginLeft: SQUAD_PAGE_HEADER.pdfSidebarPaddingHorizontal,
      marginBottom: 6,
      width: SIDEBAR_INNER_WIDTH_PT,
      height: Math.round(SIDEBAR_INNER_WIDTH_PT / SQUAD_IMAGE_FORMATS.partnerLogo.aspect),
    },
    sidebarBottomLogoImage: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
    advisorName: { fontSize: SQUAD_TYPOGRAPHY_PDF.advisorName, fontFamily: 'SegoeUI', fontWeight: 700, marginBottom: 1, color: sidebarText },
    advisorCompany: { fontSize: SQUAD_TYPOGRAPHY_PDF.advisorCompany, fontFamily: 'SegoeUI', fontWeight: 700, color: sidebarText },
    advisorBio: { fontSize: SQUAD_TYPOGRAPHY_PDF.advisorBio, lineHeight: 1.1, textAlign: 'justify', color: sidebarText },
    riskScaleLabel: { fontSize: SQUAD_TYPOGRAPHY_PDF.riskScale, color: sidebarText },
    bulletRow: { flexDirection: 'row', marginBottom: 4 },
    bulletMarker: { fontFamily: 'SegoeUI', fontWeight: 700, marginRight: 4, width: 8, color: sidebarText },
    bulletText: { fontSize: SQUAD_TYPOGRAPHY_PDF.bullet, flex: 1, lineHeight: 1.35, color: sidebarText },
    risikenNote: {
      marginTop: 6,
      fontSize: SQUAD_TYPOGRAPHY_PDF.bullet,
      lineHeight: 1.35,
      color: sidebarText,
      fontFamily: 'SegoeUI', fontStyle: 'italic',
    },
  });
};